import moment from "moment";

// Pure helpers for the Un-Synced CubiCasa Orders screen.
// The webhooks API returns only `isSynced` + `take` filtering, so all list
// filtering, sorting and pagination happen client-side over these view models.

/**
 * Extract a YYYY-MM-DD day key from an ISO datetime.
 * Returns '' for empty/invalid input.
 */
export const toDay = (iso) => {
  if (!iso) return "";
  const m = moment(iso);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

// US date-time format, e.g. "07/20/2026 06:09 AM".
const US_DATETIME = "MM/DD/YYYY hh:mm A";

/**
 * Format an epoch-seconds value (e.g. info.created_at / info.first_delivered_at)
 * as a US date-time string. Returns '' for missing/invalid input.
 */
export const formatEpochUS = (seconds) => {
  if (seconds === undefined || seconds === null || seconds === "") return "";
  const num = Number(seconds);
  if (!Number.isFinite(num)) return "";
  const m = moment(num * 1000);
  return m.isValid() ? m.format(US_DATETIME) : "";
};

/**
 * Map a raw webhook row to the flat view model the UI renders.
 * Null-safe: on `processStatus === 'Failed'` the API sends `orderDetails: null`.
 */
export const mapWebhookRow = (row = {}) => {
  const details = row.orderDetails || {};
  const info = details.info || {};
  const address = details.address || {};
  const floorplans =
    (details.delivery_assets && details.delivery_assets.listing_floorplans) || {};
  const pngUrls = Array.isArray(floorplans.png_urls) ? floorplans.png_urls : [];

  const propertyAddress = address.full_address || info.location || "";

  return {
    logId: row.logId,
    orderId: row.orderId,
    processStatus: row.processStatus,
    errorMessage: row.errorMessage || "",
    isSynced: row.isSynced,
    isFailed: row.processStatus === "Failed",
    previousStatus: row.previousStatus,

    propertyAddress,
    createdOn: row.createdOn || "",
    // "Created At" shows orderDetails.info.created_at (epoch seconds) in US
    // format; falls back to the row-level createdOn when it is absent
    // (e.g. Failed rows have no orderDetails).
    createdAtDisplay:
      formatEpochUS(info.created_at) ||
      (row.createdOn && moment(row.createdOn).isValid()
        ? moment(row.createdOn).format(US_DATETIME)
        : ""),
    // "Ready At" shows orderDetails.info.first_delivered_at (epoch seconds).
    readyAtDisplay: formatEpochUS(info.first_delivered_at),
    createdBy: info.created_by || "",
    orderType: info.order_type || "",
    totalScannedArea:
      info.total_scanned_area === undefined || info.total_scanned_area === null
        ? ""
        : info.total_scanned_area,
    styleTemplateName: (info.style_template && info.style_template.name) || "",
    city: address.city || "",
    state: address.state || "",
    floorPlanImage: pngUrls[0] || "",
  };
};

/**
 * Distinct, non-empty "created by" values as react-select options (sorted).
 */
export const buildCreatedByOptions = (viewRows = []) => {
  const seen = new Set();
  viewRows.forEach((r) => {
    if (r.createdBy) seen.add(r.createdBy);
  });
  return Array.from(seen)
    .sort()
    .map((v) => ({ value: v, label: v }));
};

/**
 * Distinct created-at days as react-select options, newest first.
 */
export const buildCreatedAtOptions = (viewRows = []) => {
  const seen = new Set();
  viewRows.forEach((r) => {
    const day = toDay(r.createdOn);
    if (day) seen.add(day);
  });
  return Array.from(seen)
    .sort()
    .reverse()
    .map((day) => ({ value: day, label: moment(day).format("MMM DD, YYYY") }));
};

/**
 * Apply the client-side filters to the mapped view rows.
 * - propertyAddress: case-insensitive substring
 * - createdBy: exact match
 * - createdAt: exact day match (YYYY-MM-DD)
 */
export const applyFilters = (viewRows = [], filters = {}) => {
  const address = (filters.propertyAddress || "").trim().toLowerCase();
  const createdBy = filters.createdBy || "";
  const createdAt = filters.createdAt || "";

  return viewRows.filter((r) => {
    if (address && !(r.propertyAddress || "").toLowerCase().includes(address)) {
      return false;
    }
    if (createdBy && r.createdBy !== createdBy) return false;
    if (createdAt && toDay(r.createdOn) !== createdAt) return false;
    return true;
  });
};

/**
 * Map a GetCustomerInfo result into react-select options for the Nancy
 * "Property Name" autosuggest. value = contactInfoId, label = property name.
 */
export const mapPropertyOptions = (result = []) =>
  (Array.isArray(result) ? result : [])
    .filter((c) => c && c.contactInfoId !== undefined && c.contactInfoId !== null)
    .map((c) => ({
      value: c.contactInfoId,
      label: c.shipToPropertyName || "(no property name)",
      contactInfoId: c.contactInfoId,
      shipToPropertyName: c.shipToPropertyName || "",
    }));

/**
 * Map a GetUnitMixDataByContactInfo result into react-select options for the
 * Nancy "Unit Type" select, de-duplicated by unitTypeId.
 */
export const mapUnitTypeOptions = (result = []) => {
  const seen = new Set();
  const out = [];
  (Array.isArray(result) ? result : []).forEach((u) => {
    if (!u || u.unitTypeId === undefined || u.unitTypeId === null) return;
    if (seen.has(u.unitTypeId)) return;
    seen.add(u.unitTypeId);
    out.push({ value: u.unitTypeId, label: u.unitType || String(u.unitTypeId) });
  });
  return out;
};

/**
 * Build the exact payload the mark-synced endpoint expects, from the selected
 * CubiCasa order row plus the chosen Nancy property + unit type.
 * POST /api/cubicasa/webhooks/{logId}/mark-synced
 */
export const buildMarkSyncedPayload = (row = {}, property, unitType) => ({
  logId: row.logId ?? null,
  orderId: row.orderId ?? null,
  nancyContactInfoId: property?.value ?? null,
  nancyUnitTypeId: unitType?.value ?? null,
});

/**
 * Client-side pagination. Clamps `page` into range and reports the display
 * window ("Showing {from} to {to} of {total}").
 */
export const paginate = (rows = [], page = 1, pageSize = 10) => {
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);
  return {
    pageRows,
    page: safePage,
    totalPages,
    total,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + pageSize, total),
  };
};
